import { Observable, Observer, throwError } from 'rxjs';
import { catchError, filter, mergeMap, take } from 'rxjs/operators';
import { LogLevel } from '../diagnostics/log-level';
import { Logging } from '../diagnostics/logging';
import { Strings } from '../generated/strings';
import { AuthorizationManager } from '../security/authorization-manager';
import {
    WebsocketStream,
    WebsocketStreamConnectionState,
    WebsocketStreamDataRequestState,
    WebsocketStreamDataState,
    WebsocketStreamDataTarget,
    WebsocketStreamHandler,
    WebsocketStreamName,
    WebsocketStreamProcessor
} from './websocket-stream';

/**
 * The SSH stream provider.
 */
export enum SshProvider {
    BeatProvider = 'beatProvider',
    ShellCommandProvider = 'shellCommandProvider',
    FileContentProvider = 'fileContentProvider',
    ProcessStatProvider = 'processStatProvider',
    PerformanceReadingProvider = 'performanceReadingProvider',
    OverviewInfoProvider = 'overviewInfoProvider',
}

/**
 * The SSH content type.
 */
export enum SshContentType {
    Text = "text",
    Error = "error",
    Json = "json",
}

/**
 * The SSH stream request type.
 */
export enum SshStreamRequestType {
    Start = "start",
    Update = "update",
    Stop = "stop",
}

/**
 * SSH stream data.
 */
export interface SshStreamData<T = any> {
    requestId?: string;
    provider: SshProvider;
    version?: string;
    time?: Date;
    payload?: {
        contentType: SshContentType;
        data: T;
    };
}

/**
 * SSH stream request data.
 * Use options for persistent queries to dynamic providers such as @see SshProvider.PerformanceReadingProvider and @see SshProvider.ProcessStatProvider.
 * If specifying options, update: true will auto update the stream request with existing parameters.
 * For manual update, use @see SshStream.updateStreamRequest.
 */
export interface SshStreamRequestData extends SshStreamData {
    parameters?: string[];
    options?: SshStreamOptions;
    update?: boolean;
}

/**
 * SSH request options. These are only needed for dynamic/persistent queries.
 * Specifying update: true  while starting a stream request will auto update the stream request with existing options.
 * For manual update, use @see SshStream.updateStreamRequest.
 */
export interface SshStreamOptions {
    action: SshStreamRequestType;
    interval: number;
    count: number;
    historyLength?: number;
}

/**
 * The request packet of the SSH Stream to the gateway.
 */
export interface SshStreamRequest {
    /**
     * The stream target.
     */
    target: WebsocketStreamDataTarget;

    /**
     * The date request state.
     */
    requestState: WebsocketStreamDataRequestState;

    /**
     * The request data.
     */
    request: SshStreamRequestData;
}

/**
 * SSH stream response.
 */
export interface SshStreamResponse {
    /**
     * The identification string (same as the requestId of the request/response data)
     * Used to map the response to the request in case the response doesn't contain the requestId.
     */
    id?: string;

    /**
     * The state of response data.
     */
    state: WebsocketStreamDataState;

    /**
     * The response data.
     * Stringified JSON representing @see SshStreamData.
     */
    response: string;
}

/**
 * SSH processor interface. Each SSH query creates new observable.
 */
class SshProcessor extends WebsocketStreamProcessor<SshStreamData> {
    private dataCount = 0;

    /**
     * Initializes a new instance of the SshProcessor class.
     * @param observer Observer to send back result to caller.
     * @param target Stream target object.
     */
    constructor(
        observer: Observer<SshStreamData>,
        target: WebsocketStreamDataTarget,
        public readonly streamRequest: SshStreamRequestData) {
        super(observer, target);
    }

    public shouldUpdateRequest(): boolean {
        this.dataCount++;
        const streamOptions = this.streamRequest.options;
        // Threshold is 80% of the count. Update the request with existing parameters if update is true.
        if (!MsftSme.isNullOrUndefined(streamOptions) && streamOptions.count > 0 && this.streamRequest.update && this.dataCount >= streamOptions.count * 0.8) {
            this.dataCount = 0;
            return true;
        }
        return false;
    }
}

/**
 * The SSH stream class.
 */
export class SshStream implements WebsocketStreamHandler {
    private static readonly logSourceName = 'SshStream';
    private static readonly sshAgentVersion = '06132023';
    private processors: Map<string /* id */, SshProcessor> = new Map();
    private strings = MsftSme.getStrings<Strings>().MsftSmeShell.Core.WebsocketStream.SshStream;

    /**
     * Initializes a new instance of the SshStream class.
     *
     * @param websocketStream the websocket stream object.
     * @param authorizationManager the authorization manager object.
     */
    constructor(
        private websocketStream: WebsocketStream,
        private authorizationManager: AuthorizationManager) {
        websocketStream.registerProcessor(WebsocketStreamName.SshStreamName, this);
    }

    /**
     * Starts/sends a request to the SSH stream.
     *
     * @param nodeName the name of the node to use for this request
     * @param request The @see SshStreamRequestData to send.
     * The @see SshStreamOptions.update will determine whether the stream auto-updates. @see SshStream.updateStreamRequest can be used to manually update the stream.
     * If the stream request is not updated, the stream will end after all data has been emitted.
     * @return Observable<SshStreamData<T>> the query observable.
     */
    public sendStreamRequest<T = any>(nodeName: string, request: SshStreamRequestData): Observable<SshStreamData<T>> {
        const requestState = WebsocketStreamDataRequestState.Normal;
        return this.createRequest(nodeName, requestState, request);
    }

    /**
     * Updates a previous SSH stream request.
     *
     * @param nodeName the name of the node to use for this request
     * @param request The @see SshStreamRequestData to send.
     * @return Observable<SshStreamData<T>> the query observable.
     */
    public updateStreamRequest(nodeName: string, request: SshStreamRequestData): void {
        const target = this.websocketStream.getTarget(this.authorizationManager, nodeName);
        const requestState = WebsocketStreamDataRequestState.Normal;
        const processor = this.processors.get(request.requestId);
        if (!processor || processor.streamRequest.provider !== request.provider) {
            Logging.log({ level: LogLevel.Warning, message: this.strings.UpdateError.message, source: SshStream.logSourceName });
            return;
        }
        const updateRequestData = this.fullRequest(request);
        const updateRequest = <SshStreamRequest>{ target, requestState, request: updateRequestData };
        this.websocketStream.sendNext(WebsocketStreamName.SshStreamName, updateRequest);
    }

    /**
     * Cancel active SSH query.
     * Result response comes back to the original query to end.
     *
     * @param nodeName the node name.
     * @param id the id of original request specified as options.queryId.
     */
    public cancel(nodeName: string, requestId: string, provider: string, parameters: string[]): void {
        const target = this.websocketStream.getTarget(this.authorizationManager, nodeName);
        const requestState = WebsocketStreamDataRequestState.Cancel;
        const cancelRequestData = <SshStreamRequestData>{
            requestId, provider, parameters, options: { action: SshStreamRequestType.Stop, interval: 0, count: 0 }
        };
        const cancelRequest = <SshStreamRequest>{ target, requestState, request: this.fullRequest(cancelRequestData) };
        this.websocketStream.sendNext(WebsocketStreamName.SshStreamName, cancelRequest);
    }

    /**
     * Reset data for connection cleanup.
     */
    public reset(): void {
        Logging.log({ level: LogLevel.Warning, message: this.strings.ResetError.message, source: SshStream.logSourceName });
        const processors: SshProcessor[] = [];
        this.processors.forEach((value, key, map) => processors.push(value));
        this.processors.clear();
        processors.forEach((processor, key, map) => {
            processor.error(new Error(this.strings.ResetError.message));
        });
    }

    /**
     * Process the socket message.
     *
     * @param message the socket message.
     */
    public process(message: SshStreamResponse): void {
        if (!message) {
            throw new Error(this.strings.NoContentError.message);
        }

        const innerMessage: SshStreamData = JSON.parse(message.response);

        const processor = this.processors.get(innerMessage.requestId ?? message.id);
        if (!processor) {
            Logging.log({ level: LogLevel.Debug, message: this.strings.UnexpectedReceivedError.message, source: SshStream.logSourceName });
            return;
        }

        switch (message.state) {
            case WebsocketStreamDataState.Data:
                this.operationNext(processor, innerMessage);
                break;

            case WebsocketStreamDataState.Completed:
                this.operationComplete(processor, innerMessage);
                this.operationEnd(innerMessage.requestId);
                break;

            case WebsocketStreamDataState.Error:
                this.operationError(processor, { xhr: message });
                this.operationEnd(innerMessage.requestId);
                break;

            case WebsocketStreamDataState.Noop:
                break;
        }
    }

    private operationNext(processor: SshProcessor, response: SshStreamData): void {
        processor.next(response);
        if (processor.shouldUpdateRequest()) {
            processor.streamRequest.options.action = SshStreamRequestType.Update;
            this.updateStreamRequest(processor.target.nodeName, processor.streamRequest);
        }
    }

    private operationComplete(processor: SshProcessor, response: SshStreamData): void {
        processor.next(response);
        processor.complete();
    }

    private operationError(processor: SshProcessor, error: any): void {
        processor.error(error);
    }

    private operationEnd(id: string): void {
        this.processors.delete(id);
    }

    private createRequest<T>(
        nodeName: string,
        requestState: WebsocketStreamDataRequestState,
        request: SshStreamRequestData): Observable<T> {
        // publish object is created in two ways.
        // 1) socket is connected so submit the request immediately with simple observable.
        //   (if-block and this is the most of cases.)
        // 2) socket is not connected so wait for the socket to be ready and submit the request with
        //    complex observable.
        //   (else-block and this is a few cases.)
        let publish: Observable<T>;
        if (this.websocketStream.socketStateRaw === WebsocketStreamConnectionState.Connected) {
            publish = this.createRequestSimple(nodeName, requestState, request);
        } else {
            publish = this.websocketStream.socketState
                .pipe(
                    filter(state => state === WebsocketStreamConnectionState.Connected
                        || state === WebsocketStreamConnectionState.Failed
                        || state === WebsocketStreamConnectionState.NotConfigured),
                    take(1),
                    mergeMap(state => {
                        if (state === WebsocketStreamConnectionState.Connected) {
                            return this.createRequestSimple<T>(nodeName, requestState, request);
                        }

                        return throwError(new Error(this.strings.ConnectionError.message));
                    }));
        }

        return publish
            .pipe(
                catchError((error, _) => {
                    // retry if reset connection of socket was observed.
                    if (error && error.message === this.strings.ResetError.message) {
                        return <Observable<T>>this.createRequestSimple(nodeName, requestState, request);
                    }

                    return throwError(error);
                }));
    }

    private createRequestSimple<T>(
        nodeName: string,
        requestState: WebsocketStreamDataRequestState,
        request: SshStreamRequestData): Observable<T> {
        return new Observable(observer => {
            const target = this.websocketStream.getTarget(this.authorizationManager, nodeName);
            const id = this.sendRequest(observer, target, requestState, request);
            return () => {
                const processor = this.processors.get(id);
                if (processor) {
                    processor.end = true;
                    if (!processor.closed && !processor.closing) {
                        this.cancel(processor.target.nodeName, id, processor.streamRequest.provider, processor.streamRequest.parameters ?? [""]);
                    }
                }
            };
        });
    }

    private sendRequest(
        observer: Observer<any>,
        target: WebsocketStreamDataTarget,
        requestState: WebsocketStreamDataRequestState,
        request: SshStreamRequestData): string {
        const fullRequest = this.fullRequest(request);
        const processor = new SshProcessor(observer, target, fullRequest);
        this.processors.set(fullRequest.requestId, processor);
        this.websocketStream.sendNext(WebsocketStreamName.SshStreamName, <SshStreamRequest>{ target, requestState, request: fullRequest });
        return fullRequest.requestId;
    }

    private fullRequest(request: SshStreamRequestData): SshStreamRequestData {
        const requestId = request.requestId ?? MsftSme.getUniqueId();
        const fullRequest: SshStreamRequestData = {
            version: SshStream.sshAgentVersion,
            time: new Date(),
            requestId,
            parameters: [""],
            ...request
        };
        return fullRequest;
    }
}
